import { BadgeCheck, Link2, Printer, Radio, Tag, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageShell, GuideHero } from "@/components/layout/PageShell";

const STATION = [
  "Only one controller is in pairing mode at a time. Every other controller is off.",
  "The team presses Drive in HubPilot. The hub light blinks orange — it's searching.",
  "Hold the small pair button on top of the controller until the Xbox logo flashes fast.",
  "The hub light turns green. Put the hub's number on the controller's sticker.",
  "Switch the controller off. Next team.",
];

const TROUBLE = [
  ["Hub blinks orange forever", "Controller isn't in pairing mode, needs its Bluetooth update, or was last paired to a console or phone. Re-pair at the station."],
  ["The wrong robot moves", "That controller was paired to another hub. Switch every other controller off and re-pair it at the station."],
  ["Robot stops by itself", "The failsafe kicked in — the controller dropped out (flat batteries, too far away). It reconnects on its own."],
  ["Hub isn't in the Bluetooth list", "Hub is off, already connected to another laptop, or still on LEGO firmware."],
  ["Driving feels laggy in a big hall", "Close laptop links after downloading, switch off spare controllers, keep drivers within about 5 m of their robot."],
];

const Card = ({ icon: Icon, title, children, testId }) => (
  <div className="rounded-3xl border bg-card p-6 lg:p-7" data-testid={testId}>
    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-secondary-foreground"><Icon className="h-5 w-5" /></span>
    <h3 className="mt-4 text-xl font-bold">{title}</h3>
    <div className="mt-2 space-y-2 text-sm leading-relaxed text-muted-foreground">{children}</div>
  </div>
);

export default function ClassroomGuide() {
  return (
    <PageShell testId="classroom-guide-page">
      <GuideHero eyebrow="Many robots, one room" title="Classroom pairing guide" icon={Users}>
        Thirty robots and thirty controllers in one room? Yes. Hundreds in a hall? Also yes — with a bit of routine. The radio copes fine; the trick is making sure each hub gets <i>its own</i> controller.
      </GuideHero>

      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-8 lg:py-16">
        <div className="rounded-3xl border-2 border-primary/40 bg-secondary/50 p-6 lg:p-8" data-testid="classroom-why-card">
          <h2 className="text-2xl font-bold sm:text-3xl">Why mix-ups happen</h2>
          <p className="mt-3 text-muted-foreground">
            Every Xbox controller in pairing mode looks identical over Bluetooth. A hub that's searching connects to the first one it hears. After pairing, a controller only reconnects to the hub it was last paired with — so if you pair <b className="text-foreground">one at a time</b>, everything stays sorted.
          </p>
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-2">
          <Card icon={Tag} title="1 · Name and label everything" testId="classroom-step-names">
            <p>Give each hub a short name when you install the firmware, like <code className="rounded bg-muted px-1.5 text-foreground">HP-R4-07</code>. Put the same number on a sticker on the hub <i>and</i> its controller.</p>
            <p>HubPilot's Bluetooth list shows these names, and the <b className="text-foreground">Identify</b> button flashes and beeps the hub you picked.</p>
          </Card>
          <Card icon={BadgeCheck} title="2 · Use a pairing station" testId="classroom-step-station">
            <ol className="list-decimal space-y-1.5 pl-5">{STATION.map((s) => <li key={s}>{s}</li>)}</ol>
          </Card>
          <Card icon={Link2} title="3 · Keep each pair together" testId="classroom-step-pairs">
            <p>After pairing, a controller reconnects to the hub it was last paired with. Keep each hub and its controller together, labelled with the same number.</p>
            <p>If a controller ends up paired with the wrong hub, re-pair it at the station with every other controller switched off.</p>
          </Card>
          <Card icon={Radio} title="4 · Keep the airwaves calm" testId="classroom-step-radio">
            <ul className="list-disc space-y-1.5 pl-5">
              <li>Disconnect the laptop once the setup is on the hub. The centre button starts it again.</li>
              <li>Switch off controllers that aren't being used.</li>
              <li>Never leave controllers sitting in pairing mode.</li>
              <li>Big events: pair everything in the pits before the event, away from the arena.</li>
            </ul>
          </Card>
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-bold sm:text-3xl">Quick fixes</h2>
          <div className="mt-5 divide-y rounded-3xl border bg-card" data-testid="classroom-troubleshooting">
            {TROUBLE.map(([q, a]) => (
              <div key={q} className="grid gap-1 p-5 sm:grid-cols-[240px_1fr] sm:gap-6">
                <p className="font-semibold">{q}</p>
                <p className="text-sm text-muted-foreground">{a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="no-print mt-10 flex flex-wrap items-center gap-3">
          <Button onClick={() => window.print()} className="rounded-full" data-testid="print-guide-button"><Printer className="mr-1.5 h-4 w-4" />Print this guide</Button>
          <p className="text-sm text-muted-foreground">Stick it on the wall next to your pairing station.</p>
        </div>
      </section>
    </PageShell>
  );
}
